import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import InfoPopup from './InfoPopup';
import * as FiIcons from 'react-icons/fi';

const { FiDivide, FiX, FiMinus, FiPlus, FiCornerDownLeft, FiDelete, FiClock, FiCopy, FiCheck, FiHelpCircle } = FiIcons;

const GeneralCalculator = ({ darkMode, isActive, expression, setExpression, result, setResult, history, setHistory, isFinalized, setIsFinalized }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!expression.trim()) {
      setResult('0');
      return;
    }

    try {
      let sanitized = expression
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
        .replace(/%/g, '/100');

      if (/[+\-*/÷×(]$/.test(sanitized.trim())) return;

      const evalResult = new Function(`return ${sanitized}`)();
      if (evalResult !== undefined && evalResult !== null && !isNaN(evalResult)) {
        const formatted = Number.isInteger(evalResult) 
          ? evalResult.toString() 
          : parseFloat(evalResult.toFixed(8)).toString();
        setResult(formatted);
      }
    } catch (e) {
      console.debug('Calculation error suppressed during intermediate typing');
    }
  }, [expression, setResult]);

  const clearAll = useCallback(() => {
    setExpression('');
    setResult('0');
    setIsFinalized(false);
  }, [setExpression, setResult, setIsFinalized]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const insertAtCursor = useCallback((val) => {
    const input = inputRef.current;
    if (!input) return;

    let start = input.selectionStart;
    let end = input.selectionEnd;
    let currentExpr = expression;

    // Logic changed: Instead of clearing the formula on digit press after Enter,
    // we now keep the formula so it can be edited.
    if (isFinalized) {
      // If it's an operator and they want to continue from the PREVIOUS RESULT
      if (/^[+×÷-]$/.test(val) || val === '*' || val === '/') {
        currentExpr = result;
        start = result.length;
        end = result.length;
      }
      // If it's a digit or anything else, we just keep the current expression 
      // so they can modify it immediately.
      setIsFinalized(false);
    }

    const newExpr = currentExpr.substring(0, start) + val + currentExpr.substring(end);
    setExpression(newExpr);

    setTimeout(() => {
      input.setSelectionRange(start + val.length, start + val.length);
      input.focus();
    }, 0);
  }, [expression, isFinalized, result, setExpression, setIsFinalized]);

  const handleFinalize = useCallback(() => {
    if (!expression || result === 'Error' || isFinalized) return;
    setHistory(prev => [{ id: Date.now(), eq: expression, res: result }, ...prev].slice(0, 20));
    setIsFinalized(true);
  }, [expression, result, isFinalized, setHistory, setIsFinalized]);

  const backspace = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    if (isFinalized) {
      setIsFinalized(false);
      return;
    }

    const start = input.selectionStart;
    const end = input.selectionEnd;

    if (start !== end) {
      const newExpr = expression.substring(0, start) + expression.substring(end);
      setExpression(newExpr);
      setTimeout(() => input.setSelectionRange(start, start), 0);
    } else if (start > 0) {
      const newExpr = expression.substring(0, start - 1) + expression.substring(start);
      setExpression(newExpr);
      setTimeout(() => input.setSelectionRange(start - 1, start - 1), 0);
    }
  }, [expression, isFinalized, setExpression, setIsFinalized]);

  useEffect(() => {
    if (!isActive) return;

    const handleGlobalKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' && e.target !== inputRef.current) return;

      const keyMap = { '*': '×', '/': '÷', 'x': '×', 'X': '×' };
      
      if (/^[0-9.()+-]$/.test(e.key)) {
        e.preventDefault();
        insertAtCursor(e.key);
      } else if (keyMap[e.key]) {
        e.preventDefault();
        insertAtCursor(keyMap[e.key]);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) clearAll();
        else handleFinalize();
      } else if (e.key === 'Backspace' && document.activeElement !== inputRef.current) {
        e.preventDefault();
        backspace();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearAll();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isActive, insertAtCursor, handleFinalize, backspace, clearAll]);

  const CalcButton = ({ value, onClick, variant = 'default', icon, className = "" }) => {
    const variants = {
      default: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600",
      operator: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40",
      action: "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/50",
    };

    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        className={`h-14 rounded-xl flex items-center justify-center text-lg font-semibold transition-all duration-200 active:scale-95 select-none ${variants[variant]} ${className}`}
      >
        {icon ? <SafeIcon icon={icon} /> : value}
      </button>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg mx-auto bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700 transition-colors duration-300"
      >
        <div className="p-8 pb-4">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-1.5 h-1.5 rounded-full ${isFinalized ? 'bg-green-500' : 'bg-blue-500'} transition-colors duration-300`} />
              <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">{isFinalized ? 'Result Locked' : 'Live Formula'}</span>
            </div>
            <div className="flex space-x-1">
              <button 
                onClick={() => setPopupType('calc-disclaimer')}
                className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                title="Disclaimer"
              >
                <SafeIcon icon={FiHelpCircle} className="text-sm" />
              </button>
              {/* Swapped order: History icon first, then Copy on the far right */}
              <button onClick={() => setShowHistory(!showHistory)} className={`p-2 rounded-full transition-all ${showHistory ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                <SafeIcon icon={FiClock} className="text-sm" />
              </button>
              <button onClick={copyToClipboard} className={`p-2 rounded-full transition-all ${copied ? 'bg-green-500 text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-sm" />
              </button>
            </div>
          </div>

          <div className="flex flex-col items-end min-h-[140px] justify-center relative group">
            <input
              ref={inputRef}
              type="text"
              value={expression}
              onChange={(e) => {
                const val = e.target.value;
                if (/^[0-9+\-*/÷×().%\s]*$/.test(val)) {
                  setExpression(val);
                  setIsFinalized(false);
                }
              }}
              placeholder="0"
              className="w-full bg-transparent border-none text-right text-gray-400 dark:text-gray-500 font-medium focus:ring-0 focus:outline-none p-0 text-2xl mb-2 placeholder-gray-200 dark:placeholder-gray-700"
              spellCheck="false"
              autoComplete="off"
            />
            <div className={`text-6xl font-black text-right tracking-tighter truncate w-full transition-all duration-300 ${isFinalized ? 'text-blue-600 dark:text-blue-400 scale-105 origin-right' : 'text-gray-900 dark:text-white'}`}>
              {result}
            </div>
          </div>
        </div>

        <div className="relative">
          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="px-8 pb-4 bg-gray-50/50 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-800"
              >
                <div className="flex justify-between items-center py-3">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">History</span>
                  {history.length > 0 && <button onClick={() => setHistory([])} className="text-[10px] font-bold text-red-500 hover:underline">Clear All</button>}
                </div>
                <div className="max-h-40 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                  {history.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4 italic">No history yet</p>
                  ) : (
                    history.map(item => (
                      <div 
                        key={item.id} 
                        onClick={() => {
                          setExpression(item.eq);
                          setIsFinalized(false);
                        }}
                        className="text-right p-3 rounded-2xl hover:bg-white dark:hover:bg-gray-700 shadow-sm transition-all cursor-pointer group border border-transparent hover:border-gray-100 dark:hover:border-gray-600"
                      >
                        <div className="text-[11px] text-gray-400 group-hover:text-blue-500 transition-colors">{item.eq}</div>
                        <div className="text-base font-bold text-gray-700 dark:text-gray-200">={item.res}</div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="p-8 pt-4 grid grid-cols-4 gap-4">
            <CalcButton value="AC" variant="action" onClick={clearAll} />
            <CalcButton value="(" variant="operator" onClick={() => insertAtCursor('(')} />
            <CalcButton value=")" variant="operator" onClick={() => insertAtCursor(')')} />
            <CalcButton icon={FiDivide} variant="operator" onClick={() => insertAtCursor('÷')} />

            <CalcButton value="7" onClick={() => insertAtCursor('7')} />
            <CalcButton value="8" onClick={() => insertAtCursor('8')} />
            <CalcButton value="9" onClick={() => insertAtCursor('9')} />
            <CalcButton icon={FiX} variant="operator" onClick={() => insertAtCursor('×')} />

            <CalcButton value="4" onClick={() => insertAtCursor('4')} />
            <CalcButton value="5" onClick={() => insertAtCursor('5')} />
            <CalcButton value="6" onClick={() => insertAtCursor('6')} />
            <CalcButton icon={FiMinus} variant="operator" onClick={() => insertAtCursor('-')} />

            <CalcButton value="1" onClick={() => insertAtCursor('1')} />
            <CalcButton value="2" onClick={() => insertAtCursor('2')} />
            <CalcButton value="3" onClick={() => insertAtCursor('3')} />
            <CalcButton icon={FiPlus} variant="operator" onClick={() => insertAtCursor('+')} />

            <CalcButton value="0" onClick={() => insertAtCursor('0')} />
            <CalcButton value="." onClick={() => insertAtCursor('.')} />
            <CalcButton icon={FiDelete} variant="action" onClick={backspace} />
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                handleFinalize();
              }}
              className={`h-14 rounded-xl flex items-center justify-center shadow-lg active:scale-95 transition-all duration-200 ${isFinalized ? 'bg-green-600 shadow-green-500/30' : 'bg-blue-600 shadow-blue-500/30'} text-white`}
            >
              <SafeIcon icon={FiCornerDownLeft} />
            </button>
          </div>
        </div>
      </motion.div>

      <InfoPopup isOpen={popupType !== null} onClose={() => setPopupType(null)} type={popupType} darkMode={darkMode} />
    </>
  );
};

export default GeneralCalculator;